import React, { useMemo } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { SplineChartV2, SplineChartV2Series, SplineChartV2DataPoint } from 'src/components/BeaconV2Components/SplineChartV2';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName,
  useMetricFormatter
} from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange, safeDivide } from 'src/utils/app';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertLineChartSeriesToDelimitedData, {
  Series
} from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import saveAs from 'file-saver';
import { useCompanyName, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import _capitalize from 'lodash/capitalize';
import {
  useBrandClassificationSplineMetrics,
  GetBrandClassificationMetricsResponse
} from './hooks/useBrandClassificationMetrics';
import { TrafficType } from '../types';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { isTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { weekIdToTimestamp } from 'src/utils/dateUtils';

function getWeekIdToTotalTrafficMap(data: GetBrandClassificationMetricsResponse, trafficType: TrafficType) {
  return data.records.reduce((acc, record) => {
    if (!acc.has(record.weekId)) {
      acc.set(record.weekId, 0);
    }
    acc.set(record.weekId, acc.get(record.weekId) + record[trafficType]);
    return acc;
  }, new Map<number, number>());
}

/**
 * Parse the response to SplineChartV2DataPoint format
 */
function getPercentTrafficSplineDataV2(
  data: GetBrandClassificationMetricsResponse,
  branded: boolean,
  trafficType: TrafficType
): SplineChartV2DataPoint[] {
  const weekIdToTotalTrafficMap = getWeekIdToTotalTrafficMap(data, trafficType);

  return data.records
    .filter((record) => (branded ? record.isBranded : !record.isBranded))
    .sort((a, b) => a.weekId - b.weekId)
    .map((record) => ({
      weekId: record.weekId,
      value: safeDivide(record[trafficType], weekIdToTotalTrafficMap.get(record.weekId) ?? 0)
    }));
}


function getTrafficTotalPercentage(
  data: GetBrandClassificationMetricsResponse,
  branded: boolean,
  trafficType: TrafficType
) {
  const weekIdToTotalTrafficMap = getWeekIdToTotalTrafficMap(data, trafficType);

  const { filteredTraffic, [trafficType]: trafficValue } = data.records
    .filter((record) => (branded ? record.isBranded : !record.isBranded))
    .reduce(
      (acc, record) => {
        return {
          [trafficType]: acc[trafficType] + (weekIdToTotalTrafficMap.get(record.weekId) ?? 0),
          filteredTraffic: acc.filteredTraffic + record[trafficType]
        };
      },
      { [trafficType]: 0, filteredTraffic: 0 }
    );

  return safeDivide(filteredTraffic, trafficValue);
}

enum PercentTrafficDropdownOption {
  Branded = 'branded',
  Unbranded = 'unbranded'
}

export interface PercentTotalTrafficSplineChartProps {
  trafficType: TrafficType;
}

/**
 * % Total Traffic Spline Chart, should only be visible at the topic
 * and topic group levels
 */
export const PercentTotalTrafficSplineChart = ({ trafficType }: PercentTotalTrafficSplineChartProps) => {
  // We only show at topic or topic group level
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const mainPeriodDisplay = mainTimePeriod.displayName;
  const comparisonPeriodDisplay = comparisonTimePeriod.displayName;
  const entityType = useMainEntityType();

  const isTopic = isTopicOrTopicGroupType(entityType);

  const [selectedDropdownId, setSelectedDropdownId] = React.useState<PercentTrafficDropdownOption>(
    PercentTrafficDropdownOption.Unbranded
  );
  const isBranded = selectedDropdownId === PercentTrafficDropdownOption.Branded;
  const formatMetric = useMetricFormatter();

  const { response, isLoading } = useBrandClassificationSplineMetrics();

  // Convert to SplineChartV2 data format
  const primaryData = useMemo(
    () => getPercentTrafficSplineDataV2(response[0], isBranded, trafficType),
    [response, isBranded, trafficType]
  );
  const comparisonData = useMemo(
    () => getPercentTrafficSplineDataV2(response[1], isBranded, trafficType),
    [response, isBranded, trafficType]
  );

  // Calculate totals using the utility function
  // For percentage metrics, we use the custom total calculation based on traffic totals
  const primaryTotal = useMemo(
    () => getTrafficTotalPercentage(response[0], isBranded, trafficType),
    [response, isBranded, trafficType]
  );
  const comparisonTotal = useMemo(
    () => getTrafficTotalPercentage(response[1], isBranded, trafficType),
    [response, isBranded, trafficType]
  );

  const percentageChange = calculatePercentChange(primaryTotal, comparisonTotal);

  // Build SplineChartV2 series
  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: `percent-${trafficType}-primary`,
      name: 'Primary',
      data: primaryData
    }),
    [trafficType, primaryData]
  );

  const comparisonSeries: SplineChartV2Series = useMemo(
    () => ({
      id: `percent-${trafficType}-comparison`,
      name: 'Comparison',
      data: comparisonData
    }),
    [trafficType, comparisonData]
  );

  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();

  const trafficTitle = {
    totalTraffic: 'Total Traffic',
    organicTraffic: 'Organic Traffic',
    paidTraffic: 'Paid Traffic'
  }[trafficType];

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const companyName = useCompanyName();

  const getSubtitle = () => {
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the percentage of ${trafficTitle.toLowerCase()} that is ${isBranded ? 'branded' : 'unbranded'
            } ${!isTopic ? ` for ${entityPhrase}` : ` for ${companyName}`}`}
          {taxonomyText}
          {isTopic ? ` in ${entityPhrase}` : ''}
          {` during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        try {
          const isPriorYear = comparisonTimePeriod.id !== 'prior-period';
          const isPriorPeriod = comparisonTimePeriod.id === 'prior-period';

          const primaryExport = primaryData.map((point) => ({
            x: weekIdToTimestamp(point.weekId) || 0,
            y: point.value ?? 0
          }));

          const comparisonExport = comparisonData
            .map((point, index) => {
              if (!isPriorYear) {
                return { x: weekIdToTimestamp(point.weekId) || 0, y: point.value ?? 0 };
              }
              const alignedX = primaryData[index]
                ? weekIdToTimestamp(primaryData[index].weekId) || 0
                : weekIdToTimestamp(point.weekId + 100) || 0;
              return { x: alignedX, y: point.value ?? 0 };
            })
            .filter((d) => d.x > 0);

          const data = convertLineChartSeriesToDelimitedData(
            [
              {
                type: 'spline',
                name: comparisonTimePeriod.displayName,
                data: comparisonExport
              },
              {
                type: 'spline',
                name: mainTimePeriod.displayName,
                data: primaryExport
              }
            ] as Series[],
            isPriorPeriod
          );

          const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
          saveAs(blob, `% ${trafficTitle} that is ${_capitalize(selectedDropdownId)}.csv`);
        } catch (e) {
          console.error('error saving % traffic CSV: ', e);
        }
      }}
      title=""
      includeSummaryInfo={false}
      loading={isLoading}
      loadingComponent={<SplineChartLoading />}
      subtitle={getSubtitle()}
      singleDropdownProps={{
        prefix: `% ${trafficTitle} that is`,
        options: [
          { id: 'branded', label: 'Branded' },
          { id: 'unbranded', label: 'Unbranded' }
        ],
        selectedId: selectedDropdownId,
        onChange: (option) => setSelectedDropdownId(option.id as PercentTrafficDropdownOption)
      }}
      primaryLegendProps={{
        displayName: mainPeriodDisplay,
        metric: formatMetric(primaryTotal, METRICTYPE.PERCENT, {
          decimalPlaces: 2
        }),
        change: formatMetric(Math.abs(percentageChange), METRICTYPE.PERCENT, {
          decimalPlaces: 2
        }),
        direction: getDirection(percentageChange)
      }}
      comparisonLegendProps={{
        displayName: comparisonPeriodDisplay,
        metric: formatMetric(comparisonTotal, METRICTYPE.PERCENT, {
          decimalPlaces: 2
        })
      }}
    >
      <SplineChartV2
        primarySeries={primarySeries}
        comparisonSeries={comparisonSeries}
        metricType={METRICTYPE.PERCENT}
      />
    </BeaconChartWithLegend>
  );
};
